import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
} from "lucide-react";
import { useEmployees } from "../../../hooks/useEmployees";
import {
  bulkUploadAttendance,
  fetchMyOrganisationSummary,
  normalizeAttendanceStatus,
} from "../../../Moduels/attendance/attendanceApi";
import {
  buildAttendanceBulkRows,
  buildEmployeeLookupMaps,
  parseAttendanceSpreadsheet,
} from "../../../Moduels/attendance/attendanceBulkParse";

export default function AttendanceBulkUpload({ embedded = false, onUploadSuccess, onStageChange }) {
  const [previewRows, setPreviewRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [columnError, setColumnError] = useState("");
  const [parseMeta, setParseMeta] = useState(null);
  const [lastParsed, setLastParsed] = useState(null);
  const [organisationId, setOrganisationId] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [orgLoading, setOrgLoading] = useState(true);
  const [upsert, setUpsert] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState("select");
  const [successMsg, setSuccessMsg] = useState("");
  const { employees, loading: employeesLoading, refresh } = useEmployees();

  const { codeToId, idToName, idToCode } = useMemo(
    () => buildEmployeeLookupMaps(employees, organisationId),
    [employees, organisationId]
  );

  useEffect(() => {
    (async () => {
      try {
        const org = await fetchMyOrganisationSummary();
        setOrganisationId(org?.id || "");
        setOrganisationName(org?.name || "Your organisation");
        refresh();
      } catch (e) {
        setError(e.message || "Failed to load organisation");
      } finally {
        setOrgLoading(false);
      }
    })();
  }, [refresh]);

  const applyParsedSheet = (headers, rows, sheetName) => {
    const built = buildAttendanceBulkRows({
      headers,
      rows,
      codeToId,
      idToName,
      idToCode,
      organisationId,
      normalizeStatus: normalizeAttendanceStatus,
      debug: true,
    });
    setParseMeta({ headers, sheetName, totalFileRows: rows.length });
    setLastParsed({ headers, rows, sheetName });
    if (!built.ok) {
      setColumnError(built.columnError || "employee_code column missing");
      setPreviewRows([]);
      setParseErrors([]);
      return;
    }
    setColumnError("");
    setParseErrors(built.errors);
    setPreviewRows(built.parsed);
  };

  useEffect(() => {
    if (!lastParsed || employeesLoading || !organisationId) return;
    applyParsedSheet(lastParsed.headers, lastParsed.rows, lastParsed.sheetName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeToId, organisationId, employeesLoading]);

  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true);
    setError("");
    setResult(null);
    setSuccessMsg("");
    setColumnError("");
    setParseErrors([]);
    setPreviewRows([]);
    setParseMeta(null);

    try {
      const [parsed] = await Promise.all([
        parseAttendanceSpreadsheet(file),
        new Promise((r) => setTimeout(r, 3600)),
      ]);
      const { headers, rows, sheetName } = parsed;
      applyParsedSheet(headers, rows, sheetName);
      setStage("preview");
      onStageChange?.("preview");
    } catch (e) {
      setError(e.message || "Failed to parse file");
      setStage("select");
      onStageChange?.("select");
    } finally {
      setParsing(false);
    }
  };

  const validRows = useMemo(
    () => previewRows.filter((r) => r.employee_id && r.work_date),
    [previewRows]
  );

  const canSubmit = useMemo(
    () =>
      Boolean(organisationId) &&
      validRows.length > 0 &&
      !employeesLoading &&
      !orgLoading &&
      !columnError,
    [organisationId, validRows.length, employeesLoading, orgLoading, columnError]
  );

  const submit = async () => {
    setError("");
    setResult(null);
    setSuccessMsg("");
    setSubmitting(true);
    try {
      const [res] = await Promise.all([
        bulkUploadAttendance({
          organisation_id: organisationId,
          records: validRows.map(({ employee_id, work_date, status, work_hours, remarks, time_in, time_out }) => ({
            employee_id,
            work_date,
            status,
            work_hours,
            remarks,
            time_in: time_in || undefined,
            time_out: time_out || undefined,
          })),
          upsert,
        }),
        new Promise((r) => setTimeout(r, 4000)),
      ]);
      setResult(res);
      setStage("result");
      onStageChange?.("result");

      const created = res?.created || 0;
      const updated = res?.updated || 0;
      setSuccessMsg(`Successfully uploaded ${created + updated} attendance record(s). Created: ${created}, Updated: ${updated}`);

      setTimeout(() => {
        onUploadSuccess?.(validRows);
      }, 5500);
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={
        embedded
          ? "att-bulk-upload w-full max-w-full min-w-0 space-y-5"
          : "min-h-screen bg-gray-50 p-8 w-full max-w-full min-w-0"
      }
    >
      {!embedded ? (
        <div className="bg-white p-6 rounded-xl shadow-md mb-6">
          <h2 className="text-2xl font-bold">Attendance Bulk Upload</h2>
          <p className="text-sm text-gray-500 mt-1">
            CSV or Excel import scoped to your logged-in organisation.
          </p>
        </div>
      ) : null}

      <div className={embedded ? "w-full" : "bg-white p-6 rounded-xl shadow-md mb-6"}>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_auto] gap-4 items-end">
          <div className="text-sm min-w-0">
            <span className="text-gray-500 block mb-1">Organisation</span>
            {orgLoading ? (
              <span className="text-gray-400 inline-flex items-center gap-1.5">
                <Loader2 size={14} className="animate-spin" /> Loading...
              </span>
            ) : organisationId ? (
              <span className="font-medium text-slate-800">
                {organisationName}
                <span className="block text-xs text-gray-400 font-normal mt-0.5">
                  Only employees in this organisation are matched ({codeToId.size} codes loaded)
                </span>
              </span>
            ) : (
              <span className="text-red-600">Organisation not found</span>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm whitespace-nowrap">
            <input type="checkbox" checked={upsert} onChange={(e) => setUpsert(e.target.checked)} />
            Upsert duplicates (employee + work_date)
          </label>

          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit || stage !== "preview" || submitting}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm whitespace-nowrap inline-flex items-center gap-2 ${
              canSubmit && stage === "preview" && !submitting
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? "Uploading..." : `Upload ${validRows.length} row(s)`}
          </button>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-50 shrink-0">
            {parsing ? (
              <>
                <Loader2 size={14} className="animate-spin text-blue-600" />
                <span className="text-blue-600">Parsing file...</span>
              </>
            ) : (
              <span>Choose File</span>
            )}
            <input
              type="file"
              accept=".csv,.xlsx"
              disabled={parsing || employeesLoading || orgLoading || submitting}
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="sr-only"
            />
          </label>
          {parsing && (
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <Loader2 size={16} className="animate-spin" />
              <span>Reading and parsing your file...</span>
            </div>
          )}
        </div>

        <div className="text-xs text-gray-500 mt-3">
          Header example:{" "}
          <code>employee_code,work_date,status,work_hours,remarks</code>
          <span className="block mt-1">
            Dates are sent as <code>YYYY-MM-DD</code> (Excel values like 3/1/28 are converted automatically).
          </span>
        </div>

        {employeesLoading ? (
          <div className="mt-2 text-xs text-gray-400 inline-flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" /> Loading employee codes for your organisation...
          </div>
        ) : null}

        {error ? (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
            <XCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
            <span className="text-sm text-red-700 font-medium">{error}</span>
          </div>
        ) : null}
        {columnError ? (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
            <AlertTriangle size={16} className="text-red-600 shrink-0 mt-0.5" />
            <span className="text-sm text-red-700 font-medium">{columnError}</span>
          </div>
        ) : null}
        {parseErrors.length > 0 ? (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
            <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <span className="text-sm text-amber-800">
              {parseErrors.length} row(s) skipped:{" "}
              {parseErrors
                .slice(0, 5)
                .map((e) => `row ${e.row}: ${e.error}`)
                .join("; ")}
              {parseErrors.length > 5 ? ` (+${parseErrors.length - 5} more)` : ""}
            </span>
          </div>
        ) : null}

        {successMsg ? (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
            <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <span className="text-sm text-emerald-800 font-semibold block">{successMsg}</span>
              <span className="text-xs text-emerald-600 mt-1 inline-flex items-center gap-1.5">
                <Loader2 size={12} className="animate-spin" /> Redirecting to Attendance Calendar...
              </span>
            </div>
          </div>
        ) : null}

        {submitting ? (
          <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-200">
            <Loader2 size={16} className="animate-spin text-blue-600" />
            <span className="text-sm text-blue-700 font-medium">Uploading attendance records, please wait...</span>
          </div>
        ) : null}

        {result && !successMsg ? (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
            <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
            <span className="text-sm text-emerald-700">
              Uploaded. Created: {result.created}, Updated: {result.updated}, Errors:{" "}
              {Array.isArray(result.errors) ? result.errors.length : 0}
            </span>
          </div>
        ) : null}
      </div>

      {parseMeta && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 mb-4">
          Parsed headers: <code>{parseMeta.headers.join(", ")}</code>
          {parseMeta.sheetName ? (
            <span className="ml-2">· Sheet: {parseMeta.sheetName}</span>
          ) : null}
          <span className="ml-2">· Data rows in file: {parseMeta.totalFileRows}</span>
        </div>
      )}

      <div className={embedded ? "w-full" : "bg-white rounded-xl shadow-md p-6"}>
        <h3 className="font-semibold mb-4">
          Preview {stage === "preview" ? "(verify before upload)" : ""}
        </h3>

        <div className="w-full overflow-x-auto -mx-1 px-1">
        <table className="w-full min-w-[640px] text-sm border">
          <thead className="bg-gray-100">
            <tr>
              <th className="border p-2">Row</th>
              <th className="border p-2">Employee</th>
              <th className="border p-2">Code</th>
              <th className="border p-2">Work Date</th>
              <th className="border p-2">Status</th>
              <th className="border p-2">Check In</th>
              <th className="border p-2">Check Out</th>
              <th className="border p-2">Hours</th>
              <th className="border p-2">Result</th>
            </tr>
          </thead>
          <tbody>
            {previewRows.length === 0 && parseErrors.length === 0 ? (
              <tr>
                <td colSpan={9} className="border p-4 text-center text-gray-400">
                  Upload a file to see parsed rows
                </td>
              </tr>
            ) : null}
            {previewRows.map((d) => (
              <tr key={`${d.row}-${d.work_date}`} className="bg-emerald-50/40">
                <td className="border p-2">{d.row}</td>
                <td className="border p-2">{d.employee_name || "—"}</td>
                <td className="border p-2">{d.employee_code || "—"}</td>
                <td className="border p-2">
                {d.work_date}
                {d.work_date_raw ? (
                  <span className="block text-xs text-slate-400">from {d.work_date_raw}</span>
                ) : null}
              </td>
                <td className="border p-2">
                  {d.preview_status} → {d.status}
                </td>
                <td className="border p-2">{d.check_in || "—"}</td>
                <td className="border p-2">{d.check_out || "—"}</td>
                <td className="border p-2">{d.work_hours}</td>
                <td className="border p-2 text-emerald-700 font-medium">OK</td>
              </tr>
            ))}
            {parseErrors.map((e) => (
              <tr key={`err-${e.row}`} className="bg-red-50">
                <td className="border p-2">{e.row}</td>
                <td className="border p-2">{e.employee_code || "—"}</td>
                <td className="border p-2">—</td>
                <td className="border p-2">—</td>
                <td className="border p-2">—</td>
                <td className="border p-2">—</td>
                <td className="border p-2">—</td>
                <td className="border p-2">—</td>
                <td className="border p-2 text-red-700">{e.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
